/* eslint-disable @typescript-eslint/no-explicit-any */

import { API_BASE_URL } from "@/config/api";
import axios from "axios";
import Cookies from "js-cookie";
import https from "node:https";

const keepAliveAgent = new https.Agent({
    keepAlive: true,
    maxSockets: 50,         // bounded so we can't OOM the backend
    maxFreeSockets: 20,
    keepAliveMsecs: 30_000,
});

export const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    httpsAgent: keepAliveAgent,
    headers: { 'Content-Type': 'application/json', userID: '' },
    timeout: 30_000,
});

axiosInstance.interceptors.request.use((config: any) => {
    const token = Cookies.get('auth_token') || '';
    const deviceId = Cookies.get('device') || '';
    const userId = Cookies.get('userID') || '';

    config.headers['Authorization'] = `Bearer ${token}`;
    config.headers['device'] = deviceId;
    config.headers['userID'] = userId;

    // Default Content-Type is application/json; strip it for FormData so the
    // browser can set multipart/form-data with the correct boundary.
    const isFormData =
        typeof FormData !== "undefined" && config.data instanceof FormData;
    if (isFormData && config.headers) {
        if (typeof config.headers.delete === "function") {
            config.headers.delete("Content-Type");
            config.headers.delete("content-type");
        } else {
            delete config.headers["Content-Type"];
            delete config.headers["content-type"];
        }
        if (typeof config.headers.set === "function") {
            config.headers.set("Content-Type", false);
        }
    }

    return config;
}, (error: any) => {
    return Promise.reject(error);
});


export interface AxiosConnectorOptions {
    /** When false, returns null instead of throwing on error. Prevents one failed API from crashing the whole page. */
    throwOnError?: boolean;
    /** Total attempts on transient failures (timeout / no response / 5xx). Default 3 (one initial + two retries). */
    maxAttempts?: number;
}

/**
 * Decide whether an error is worth retrying. We retry only on signals that look
 * like transient backend / network blips — 5xx, timeouts, DNS resolution
 * failures, dropped sockets. 4xx are deliberate negative answers and never
 * retried.
 */
const isRetryableError = (error: any): boolean => {
    const status = error?.response?.status;
    if (typeof status === "number") {
        return status >= 500 && status <= 599;
    }
    const code = error?.code;
    // ECONNABORTED == axios timeout; the rest are node-level transport blips.
    return (
        code === "ECONNABORTED" ||
        code === "ECONNRESET" ||
        code === "ETIMEDOUT" ||
        code === "EAI_AGAIN" ||
        code === "ENOTFOUND" ||
        code === "EHOSTUNREACH" ||
        code === "ENETUNREACH"
    );
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export const axiosConnector = async (
    method: 'get' | 'post' | 'put' | 'delete' | 'patch',
    end_point: string,
    bodyData: any = null,
    headers?: Record<string, any>,
    params?: Record<string, any>,
    options?: AxiosConnectorOptions
) => {
    const throwOnError = options?.throwOnError ?? false;
    const maxAttempts = Math.max(1, options?.maxAttempts ?? 3);

    const isFormData =
        typeof FormData !== "undefined" && bodyData instanceof FormData;

    let lastError: any;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const requestHeaders: Record<string, any> = {
                ...axiosInstance.defaults.headers.common,
                ...(headers || {}),
            };

            // Let the browser set multipart boundary for FormData uploads.
            if (isFormData) {
                delete requestHeaders["Content-Type"];
                delete requestHeaders["content-type"];
            }

            const response = await axiosInstance({
                method,
                url: end_point,
                data: bodyData,
                headers: requestHeaders,
                params,
            });
            return response?.data;
        } catch (error: any) {
            lastError = error;

            // Handle 404 using Next.js logic — never retry.
            if (error?.response?.status === 404) {
                if (typeof window !== "undefined") {
                    window.location.href = "/404";
                }
                break;
            }

            // Retry budget exhausted, or non-retryable (4xx other than 404).
            if (attempt >= maxAttempts || !isRetryableError(error)) {
                break;
            }

            // Exponential backoff with light jitter: 300ms, 900ms, 2700ms…
            const baseDelay = 300 * Math.pow(3, attempt - 1);
            const jitter = Math.floor(Math.random() * 200);
            await sleep(baseDelay + jitter);
        }
    }

    if (!throwOnError) {
        return null;
    }
    throw lastError;
};